import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../../utils/tpoGenQueryKeys';

export interface ApiConstraintLevelDetail {
    attribute_name: string;
    original_value: number | null;
    default_value: number | null;
    full_year_original_value?: number | null;
    constraint: { min: number; max: number } | null;
}

export interface ApiConstraintDetails {
    constraint_name: string;
    label: string;
    constraint_type: 'range';
    level_details: ApiConstraintLevelDetail[];
    objective_constraint?: boolean;
}

export interface ConstraintsDataResponse {
    constraint_details: ApiConstraintDetails[];
}

interface RawLevelDetail {
    attribute_name: string;
    details?: Record<string, unknown>;
    [key: string]: unknown;
}

interface RawConstraintDetail {
    name?: string;
    constraint_name?: string;
    label: string;
    constraint_type?: string;
    level_details: RawLevelDetail[];
    objective_constraint?: boolean;
}

function normalizeLevelDetail(raw: RawLevelDetail): ApiConstraintLevelDetail {
    if (raw.details && typeof raw.details === 'object') {
        return {
            attribute_name: raw.attribute_name,
            original_value: (raw.details.original_value as number) ?? null,
            default_value: (raw.details.default_value as number) ?? null,
            full_year_original_value: (raw.details.full_year_original_value as number) ?? null,
            constraint: (raw.details.constraint as { min: number; max: number }) ?? null,
        };
    }
    return {
        attribute_name: raw.attribute_name,
        original_value: (raw.original_value as number) ?? null,
        default_value: (raw.default_value as number) ?? null,
        full_year_original_value: (raw.full_year_original_value as number) ?? null,
        constraint: (raw.constraint as { min: number; max: number }) ?? null,
    };
}

function normalizeResponse(raw: { data: { constraint_details: RawConstraintDetail[] } }): { data: ConstraintsDataResponse } {
    return {
        data: {
            constraint_details: raw.data.constraint_details.map((item) => ({
                constraint_name: item.constraint_name ?? item.name ?? '',
                label: item.label,
                constraint_type: (item.constraint_type as 'range') ?? 'range',
                level_details: item.level_details.map(normalizeLevelDetail),
                objective_constraint: item.objective_constraint,
            })),
        },
    };
}

const fetchConstraintsData = async (
    payload: Record<string, unknown>,
    signal?: AbortSignal,
): Promise<{ data: ConstraintsDataResponse }> => {
    const response = await axios(tpoGenUrls.getConstraintsListOriginalValues, {
        method: 'POST',
        data: payload,
        signal,
    });
    return normalizeResponse(response.data);
};

const useConstraintsData = ({
    payload,
    enabled,
}: {
    payload: Record<string, unknown> | null;
    enabled: boolean;
}) => {
    const { data, isLoading, isError } = useQuery({
        queryKey: tpoGenKeys.optimizationValues(payload),
        queryFn: ({ signal }) => fetchConstraintsData(payload!, signal),
        enabled: enabled && Boolean(payload),
        refetchOnWindowFocus: false,
    });

    return {
        constraintsData: data ?? null,
        constraintsLoading: isLoading,
        constraintsError: isError,
    };
};

export default useConstraintsData;
